import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import Breadcrumbs from '@/components/Breadcrumbs';
import { getCourseById } from '@/hooks/useCourses';
import type { Lesson } from '@/hooks/useLesson';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
// ggLogo import removed: no longer showing thumbnails in chapter list
import { useEffect, useState } from 'react';

export const Route = createFileRoute('/_auth/courses/$courseId')({
  component: RouteComponent,
});

function RouteComponent() {
  const { courseId } = Route.useParams();
  const navigate = useNavigate();
  const { data: course, isLoading, isError, error } = getCourseById(courseId);
  const [isOverviewOpen, setIsOverviewOpen] = useState(false);

  // Share/Save actions removed (UI commented out). Re-introduce here if needed in future.

  // Inject CSS for continuous arrow animation
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideArrowContinuous {
        0% {
          transform: translateX(-12px);
          opacity: 0;
        }
        50% {
          opacity: 1;
        }
        100% {
          transform: translateX(12px);
          opacity: 0;
        }
      }
      .arrow-slide-animation {
        animation: slideArrowContinuous 1.5s ease-in-out infinite;
      }
      .arrow-slide-animation-delayed {
        animation: slideArrowContinuous 1.5s ease-in-out infinite 0.75s;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white max-w-[1100px] px-3 pb-10 mx-auto">
        <div className="max-w-6xl mx-auto px-4 pt-8">
          {/* Header Skeleton */}
          <div className="mb-8">
            <Skeleton height={60} width={300} className="mb-4" />
            <Skeleton count={2} height={20} width="80%" />
          </div>

          {/* Banner Skeleton */}
          <div className="mb-8">
            <Skeleton height={300} className="rounded-xl" />
          </div>

          {/* Course Info Skeleton */}
          <div className="mb-8 p-6 bg-violet-50 rounded-xl border border-violet-100">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
              {[1, 2, 3].map(i => (
                <div key={i}>
                  <Skeleton height={32} width={60} className="mb-2" />
                  <Skeleton height={16} width={100} />
                </div>
              ))}
            </div>
          </div>

          {/* Lessons Skeleton */}
          <div className="space-y-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="border border-gray-200 rounded-xl p-6">
                <div className="flex items-center">
                  <Skeleton circle height={40} width={40} className="mr-4" />
                  <div className="flex-1">
                    <Skeleton height={20} width={200} className="mb-2" />
                    <Skeleton height={16} width="60%" />
                  </div>
                  <Skeleton height={16} width={60} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    const status = (error as any)?.response?.status;
    if (status === 403) {
      navigate({ to: '/', replace: true, search: { invitationcode: undefined, __clerk_ticket: undefined } });
      return null;
    }
    return (
      <div className="min-h-screen bg-white max-w-[1100px] px-3 pb-10 mx-auto">
        <div className="max-w-6xl mx-auto px-4 pt-8">
          <div className="text-center py-12">
            <div className="text-red-500 text-lg font-semibold mb-2">
              Error loading course
            </div>
            <p className="text-gray-600">
              We couldn't load the course details. Please try refreshing the page.
            </p>
            <Link
              to="/learn"
              search={{ tab: 'Courses', tour: undefined, invitationcode: undefined, __clerk_ticket: undefined }}
              className="inline-block mt-4 bg-violet-500 text-white px-6 py-2 rounded-lg hover:bg-violet-600 transition"
            >
              Back to Courses
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-white max-w-[1100px] px-3 pb-10 mx-auto">
        <div className="max-w-6xl mx-auto px-4 pt-8">
          <div className="text-center py-12">
            <div className="text-gray-500 text-lg font-semibold mb-2">
              Course not found
            </div>
            <p className="text-gray-600">
              The course you're looking for doesn't exist or has been removed.
            </p>
            <Link
              to="/learn"
              search={{ tab: 'Courses', tour: undefined, invitationcode: undefined, __clerk_ticket: undefined }}
              className="inline-block mt-4 bg-violet-500 text-white px-6 py-2 rounded-lg hover:bg-violet-600 transition"
            >
              Back to Courses
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Calculate completion stats
  const totalLessons = course.lessons?.length || 0;
  const completedLessons = course.lessons?.filter(lesson => 
    lesson.lessonProgress?.some(progress => progress.completed)
  ).length || 0;

  // Get current user's progress for this course
  const userCourseProgress = course.courseProgress?.[0];
  const isCompleted = userCourseProgress?.completed || false;

  const sortedLessons = (course.lessons ?? [])
    .slice()
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  const progressPercentage = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;

  // Derived course details for badges
  const totalDurationMinutes = (course.lessons ?? []).reduce((total, lesson) => {
    const duration = Number(lesson?.duration ?? 0);
    return Number.isFinite(duration) ? total + duration : total;
  }, 0);

  // Create a soft range for total time to match the design inspiration (approximate)
  const hasDuration = totalDurationMinutes > 0;
  // const approxLow = hasDuration ? Math.max(1, Math.round((totalDurationMinutes * 0.85) / 5) * 5) : 0;
  // const approxHigh = hasDuration ? Math.max(approxLow, Math.round((totalDurationMinutes * 1.1) / 5) * 5) : 0;
  const approxMid = hasDuration ? Math.round(totalDurationMinutes / 5) * 5 : 0;

  // Aggregate guardian points (exp) from lessons
  const totalCourseExp = (course.lessons ?? []).reduce((sum, lesson) => {
    const exp = Number(lesson?.exp ?? 0);
    return Number.isFinite(exp) ? sum + exp : sum;
  }, 0);

  // Static formats label per request
  const formatLabel = 'Video, Text, Quiz';


  // Determine the best lesson to resume and the appropriate CTA label
  const resumeLessonFromProgress = userCourseProgress?.currentLessonId
    ? (sortedLessons.find(l => l.id === userCourseProgress!.currentLessonId) || null)
    : null;
  const firstIncompleteLesson = sortedLessons.find(l => !(l.lessonProgress?.some(p => p.completed))) || sortedLessons[0];
  const resumeLesson = resumeLessonFromProgress || firstIncompleteLesson || null;
  const resumeLessonIndex = resumeLesson ? sortedLessons.findIndex(l => l.id === resumeLesson.id) : -1;
  const isFreshStart = completedLessons === 0 && !userCourseProgress?.currentLessonId;
  const startButtonLabel = isFreshStart
    ? (resumeLessonIndex >= 0 ? `Start Chapter ${resumeLessonIndex + 1}: ${resumeLesson?.name}` : 'Start')
    : (resumeLessonIndex >= 0 ? `Resume: ${resumeLesson?.name}` : 'Resume');


  return (
    <div className="min-h-screen bg-white max-w-[1100px] px-3 pb-10 mx-auto">
      <div className="max-w-6xl mx-auto px-4">
        {/* Breadcrumbs */}
        <Breadcrumbs
          items={[
            { label: 'Training', href: '/learn', className: 'text-violet-600 underline underline-offset-2 font-semibold', wrapperClassName: '' },
            // { label: 'Courses', href: '/courses' },
            { label: course.name },
          ]}
        />
        {/* Course Banner */}
        {/* <div className="mb-8">
          <div className="w-full lg:h-[420px] md:h-90 bg-gray-50 flex items-center justify-center overflow-hidden rounded-xl border border-gray-200">
            <img
              src={course.bannerImage || ggLogo}
              alt={course.name}
              className="w-full h-full object-cover object-center"
              onError={e => { (e.currentTarget as HTMLImageElement).src = ggLogo; }}
            />
          </div>
        </div> */}

        {/* Title, Description and Stats Layout */}
        
        <div className="flex flex-col lg:flex-row gap-6">
          
          {/* Course Title and Description - Left Side */}
          <div className="flex-1">
               {/* Badges under description */}
               <div className="flex flex-wrap gap-2 mb-4">
              {/* {hasDuration && (
                <div className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm">
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{approxLow}-{approxHigh} minutes</span>
                </div>
              )} */}
              {/* {totalLessons > 0 && (
                <div className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm">
                  <svg className="w-4 h-4 text-gray-500" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 6h2v2H4V6zm0 5h2v2H4v-2zm0 5h2v2H4v-2zm16-8V6H8.023v2H20zm0 5v-2H8.023v2H20zm0 5v-2H8.023v2H20z"/>
                  </svg>
                  <span>{totalLessons} short chapters</span>
                </div>
              )} */}
              {/* Static rating placeholder; replace with real data when available */}
              {/* <div className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm">
                <svg className="w-4 h-4 text-yellow-500" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.802 2.036a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118L10 13.347l-2.985 2.136c-.785.57-1.84-.197-1.54-1.118l1.07-3.292a1 1 0 00-.364-1.118L3.38 8.72c-.783-.57-.38-1.81.588-1.81H7.43a1 1 0 00.95-.69l1.07-3.292z" />
                </svg>
                <span>4.9/5 (1,240 reviews)</span>
              </div> */}
              {totalCourseExp > 0 && (
                <div className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm">
                  <svg className="w-4 h-4 text-violet-500" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2l2.09 6.26H20l-5.17 3.76L16.18 18 12 14.77 7.82 18l1.35-5.98L4 8.26h5.91L12 2z" />
                  </svg>
                  <span>Earns {totalCourseExp} Guardian Points</span>
                </div>
              )}
            </div>
            <div className="rounded-md pb-0 md:pb-1 text-gray-900 text-2xl sm:text-4xl md:text-5xl lg:text-5xl font-medium">
              {course.name}
            </div>
            <p className="mt-2 text-base sm:text-lg md:text-xl font-normal text-gray-700 leading-relaxed sm:leading-8 break-words">
              {course.description}
            </p>
         

            {/* Start/Continue or Completed */}
            {isCompleted ? (
              <div className="mt-5 inline-flex items-center justify-center gap-2 bg-violet-50 text-violet-800 px-6 py-3 rounded-lg font-semibold border border-violet-200">
                <svg className="w-5 h-5 inline-block " viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" className="fill-violet-600"/>
                  <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Course Complete
              </div>
            ) : (
              resumeLesson ? (
                <Link
                  to="/lessons/$lessonId"
                  params={{ lessonId: resumeLesson.id }}
                  search={{ invitationcode: undefined, __clerk_ticket: undefined }}
                  className="mt-5 inline-flex items-center justify-center gap-2 bg-violet-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-violet-500 transition w-full sm:w-auto"
                >
                  {isFreshStart ? (
                    <>
                      <span className="font-normal">Start</span>
                      <span className="font-normal">{startButtonLabel.slice(5)}</span>
                    </>
                  ) : (
                    <span>{startButtonLabel}</span>
                  )}
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </Link>
              ) : null
            )}
          </div>

          {/* Course Info - Right Side */}
          <div className="lg:w-72 bg-white rounded-xl border border-gray-200 px-4 py-2 sm:p-5 md:p-5 shadow-xs">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-medium text-gray-900 flex items-center gap-1">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Guide Overview
              </h3>
              <button
                type="button"
                className="lg:hidden inline-flex items-center justify-center w-8 h-8 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                onClick={() => setIsOverviewOpen(v => !v)}
                aria-expanded={isOverviewOpen}
                aria-controls="guide-overview-content"
              >
                <svg className={`w-5 h-5 transition-transform ${isOverviewOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 011.08 1.04l-4.25 4.25a.75.75 0 01-1.06 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd"/></svg>
              </button>
            </div>
            <div id="guide-overview-content" className={`mt-4 ${isOverviewOpen ? 'block' : 'hidden'} lg:block`}>
              {/* Progress indicator */}
              <div className="mb-4">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-gray-500">Progress</span>
                  <span className="text-gray-900 font-medium">{progressPercentage}%</span>
                </div>
                <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-violet-600" style={{ width: `${progressPercentage}%` }}></div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-y-3 text-sm">
                <div>
                  <div className="text-gray-500">Difficulty</div>
                  <div className="text-gray-900 font-medium">Beginner</div>
                </div>
                <div>
                  <div className="text-gray-500">Chapters</div>
                  <div className="text-gray-900 font-medium">{totalLessons}</div>
                </div>
                <div>
                  <div className="text-gray-500">Total time</div>
                  <div className="text-gray-900 font-medium">{hasDuration ? `~${approxMid} min` : '—'}</div>
                </div>
                <div>
                  <div className="text-gray-500">Formats</div>
                  <div className="text-gray-900 font-medium">{formatLabel}</div>
                </div>
              </div>
              <div className="my-4 border-t border-gray-200"></div>
              <p className="text-sm text-gray-600">
              Questions or issues? Visit 
                {' '}
                <Link
                  to="/contact-support"
                  search={{ invitationcode: undefined, __clerk_ticket: undefined }}
                  className="text-violet-600 hover:text-violet-800 hover:underline underline-offset-2"
                >
                  Support
                </Link>
                {' '}for quick help from the GranGuard team.
              </p>
              {/* <div className="flex gap-3">
                <button onClick={handleShare} className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-700 font-medium hover:bg-gray-50">Share</button>
                <button onClick={toggleSave} className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-700 font-medium hover:bg-gray-50">{isSaved ? 'Saved' : 'Save'}</button>
              </div> */}
              {/* Completed indicator removed from sidebar; now shown near Start/Resume */}
            </div>
          </div>
        </div>

        {/* Action Buttons moved into Course Info when completed */}

        {/* Lessons List */}
        <div className="mb-8">
          <div className="mb-4">
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 sm:pt-4 pt-4 flex items-center gap-2">
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-gray-900" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 6h16v2H4zm0 5h16v2H4zm0 5h16v2H4z"/>
              </svg>
              Chapters
            </h2>
            <p className="text-gray-600 ">Work through each chapter at your own pace.</p>
          </div>

          {totalLessons === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
              <div className="text-gray-500 text-lg font-semibold mb-2">
                No lessons available
              </div>
              <p className="text-gray-600">
                This course doesn't have any lessons yet. Check back soon!
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {sortedLessons.map((lesson: Lesson, index) => {
                const isCompleted = lesson.lessonProgress?.some(progress => progress.completed) || false;
                const isCurrent = userCourseProgress?.currentLessonId === lesson.id;
                const displayIndex = index + 1;
                const userLessonProgress = lesson.lessonProgress?.[0];
                const pagesCount = Array.isArray(lesson.pages) ? lesson.pages.length : 0;
                const currentPageId = userLessonProgress?.currentPageId || null;
                let progressPercent = 0;
                if (isCompleted) {
                  progressPercent = 100;
                } else if (pagesCount > 0 && currentPageId) {
                  const pageIndex = (lesson.pages || []).findIndex((p: any) => p?.id === currentPageId);
                  progressPercent = pageIndex >= 0 ? Math.round(((pageIndex + 1) / pagesCount) * 100) : 0;
                }
                  return (
                  <div key={lesson.id} className={`relative bg-[#F8F8FD] border border-gray-100 hover:border-violet-500 rounded-xl transition-all px-4 sm:px-6 py-3 ${isCurrent ? '' : ''}`}>
                      <Link to="/lessons/$lessonId" params={{ lessonId: lesson.id }} search={{ invitationcode: undefined, __clerk_ticket: undefined }} className="flex flex-col sm:flex-row items-start sm:items-center sm:pl-0 pl-0 sm:py-1 transition group gap-2 sm:gap-0 focus:outline-none focus:ring-0 focus:ring-violet-500 focus:ring-inset ">
                        <div className="flex-shrink-0 mr-4 sm:mr-5 hidden sm:block">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-3xl bg-violet-100 text-violet-900 border border-violet-200 flex items-center justify-center" aria-label={`Chapter ${displayIndex}`}>
                            <span className="text-sm sm:text-base font-semibold">{displayIndex}</span>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="sm:grid sm:grid-cols-3 sm:gap-4">
                            {/* Left: title + description */}
                            <div className="sm:col-span-2">
                              <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                                <span className="sm:hidden inline-flex items-center justify-center w-6 h-6 rounded-md bg-violet-600 text-white text-xs font-semibold">{displayIndex}</span>
                                <h3 className="text-base sm:text-xl md:text-xl font-semibold text-gray-900 group-hover:text-violet-700  transition">{lesson.name}</h3>
                                {isCurrent && (
                                  <span className="inline-flex items-center px-2 py-2 rounded-full text-md font-medium text-gray-900 bg-violet-50">
                                    <svg className="w-4 h-4 text-violet-600" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/></svg>
                                  </span>
                                )}
                                {/* Badges next to title */}
                                <div className="flex items-center gap-0">
                                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-white text-gray-700 border border-gray-200">
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                    {lesson.duration} min
                                  </span>
                                </div>
                              </div>
                              <p className="text-gray-600 text-sm sm:text-lg leading-relaxed">{lesson.description}</p>
                            </div>
                            {/* Right: meta column (progress centered or completed) */}
                            <div className="sm:col-span-1 mt-2 sm:mt-0 flex flex-col items-start sm:items-end sm:justify-center">
                              <div className="w-full sm:w-40 flex flex-col items-stretch">
                                {isCompleted || progressPercent === 100 ? (
                                  <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-full text-xs font-medium bg-violet-50 text-violet-700 border border-violet-200">
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                    Completed
                                  </span>
                                ) : (
                                  <>
                                    <div className="flex items-center justify-between text-xs mb-1">
                                      <span className="text-gray-500">Progress</span>
                                      <span className="text-gray-900 font-medium">{progressPercent}%</span>
                                    </div>
                                    <div className="h-1.5 w-full bg-violet-100 rounded-full overflow-hidden">
                                      <div className="h-full bg-violet-600" style={{ width: `${progressPercent}%` }}></div>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="hidden sm:block flex-shrink-0 sm:ml-4 relative w-8 h-8 sm:w-10 sm:h-10 overflow-hidden">
                          <svg className="absolute inset-0 w-full h-full text-gray-400 group-hover:opacity-0 transition-opacity duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 5l7 7-7 7" /></svg>
                          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <svg className="absolute inset-0 w-full h-full text-violet-500 arrow-slide-animation" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                            <svg className="absolute inset-0 w-full h-full text-violet-500 arrow-slide-animation-delayed" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                          </div>
                        </div>
                      </Link>
                    </div>
                  );
              })}
            </div>
          )}
        </div>


      </div>
    </div>
  );
}